/**
 * Review Service
 * Handles all review-related API calls
 *
 * @module services/ReviewService
 */

import BaseService from "core/api/BaseService";
import { API_ENDPOINTS } from "utils/constants";

class ReviewService extends BaseService {
  constructor() {
    super(API_ENDPOINTS.REVIEWS.BASE);
  }

  /**
   * Get reviews for an item (course or tutorial)
   * @param {string} itemType - Type of item ('course' or 'tutorial')
   * @param {string} itemId - Item ID
   * @param {Object} params - Query parameters
   * @returns {Promise} Reviews list
   */
  async getByItem(itemType, itemId, params = {}) {
    return this.get(API_ENDPOINTS.REVIEWS.BY_ITEM(itemType, itemId), params);
  }

  /**
   * Get my review for an item
   * @param {string} itemType - Type of item ('course' or 'tutorial')
   * @param {string} itemId - Item ID
   * @returns {Promise} My review or null
   */
  async getMyReview(itemType, itemId) {
    return this.get(API_ENDPOINTS.REVIEWS.MY_REVIEW(itemType, itemId));
  }

  /**
   * Create a review
   * @param {Object} reviewData - Review data
   * @param {string} reviewData.itemType - Type of item ('course' or 'tutorial')
   * @param {string} reviewData.itemId - Item ID
   * @param {number} reviewData.rating - Rating (1-5)
   * @param {string} reviewData.comment - Review comment
   * @returns {Promise} Created review
   */
  async createReview(reviewData) {
    return this.create(reviewData);
  }

  /**
   * Update a review
   * @param {string} reviewId - Review ID
   * @param {Object} reviewData - Updated review data
   * @returns {Promise} Updated review
   */
  async updateReview(reviewId, reviewData) {
    return this.update(reviewId, reviewData);
  }

  /**
   * Delete a review
   * @param {string} reviewId - Review ID
   * @returns {Promise} Delete result
   */
  async deleteReview(reviewId) {
    return this.delete(reviewId);
  }

  /**
   * Like a review
   * @param {string} reviewId - Review ID
   * @returns {Promise} Like result
   */
  async likeReview(reviewId) {
    return this.post(`${reviewId}/like`, {});
  }

  /**
   * Unlike a review
   * @param {string} reviewId - Review ID
   * @returns {Promise} Unlike result
   */
  async unlikeReview(reviewId) {
    return this.deleteCustom(`${reviewId}/like`);
  }

  /**
   * Report a review
   * @param {string} reviewId - Review ID
   * @param {string} reason - Report reason
   * @returns {Promise} Report result
   */
  async reportReview(reviewId, reason) {
    return this.post(`${reviewId}/report`, { reason });
  }

  /**
   * Get review statistics for an item
   * @param {string} itemType - Type of item ('course' or 'tutorial')
   * @param {string} itemId - Item ID
   * @returns {Promise} Review statistics
   */
  async getStatistics(itemType, itemId) {
    return this.get(`${itemType}/${itemId}/statistics`);
  }
}

// Export singleton instance
export default new ReviewService();

